import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import {
  ErrorResponse, IncludeOption,
  MessageTemplate,
  MessageTemplatePaginatedResponse,
  MessageTemplatePostRequest, MessageTemplatePutRequest,
} from '@clientwire/api';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class MessageTemplatesService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  readonly totalElements = signal(0)

  readonly loading = signal(false)
  readonly hasMore = signal(false)
  readonly error: WritableSignal<string | null> = signal(null)
  readonly messageTemplates: WritableSignal<MessageTemplate[] | null> = signal(null)

  loadMessageTemplates(): void {
    this.loading.set(true);
    this.error.set(null)

    this.#wireApiService.messageTemplatesApi.listMessageTemplates(
      { includes: [IncludeOption.Texts, IncludeOption.Parameters], }
    )
      .then((response: MessageTemplatePaginatedResponse) => {
        this.messageTemplates.set(response.messageTemplates);
        this.totalElements.set(response.totalElements || 0);
        this.hasMore.set(response.hasMore);
      })
      .catch(async (error) => {
        console.error('Error loading message templates:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_LOADING');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.error.set(errorMessage);
      });

    this.loading.set(false);
  }

  createMessageTemplate(templateData: any): void {
    this.loading.set(true);

    const messageTemplateRequest: MessageTemplatePostRequest = {
      name: templateData.name,
      description: templateData.description,
      kind: templateData.kind,
      texts: templateData.texts,
      parameters: templateData.parameters || []
    };

    this.#wireApiService.messageTemplatesApi.createMessageTemplate({
      messageTemplatePostRequest: messageTemplateRequest
    })
      .then(() => {
        this.loadMessageTemplates();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_ADDED_MESSAGE'),
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_ADDED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error creating message template:', error);

        let errorMessage = this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_ADDING_MESSAGE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(errorMessage, this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_ADDING_TITLE'));
      });

    this.loading.set(false);
  }

  updateMessageTemplate(templateName: string, templateData: any): void {
    this.loading.set(true);
    const messageTemplateRequest: MessageTemplatePutRequest = {
      description: templateData.description,
      kind: templateData.kind,
      texts: templateData.texts,
      parameters: templateData.parameters || []
    };

    this.#wireApiService.messageTemplatesApi.updateMessageTemplate({
      name: templateName,
      messageTemplatePutRequest: messageTemplateRequest
    })
      .then(() => {
        this.loadMessageTemplates();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_UPDATED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error updating message template:', error);

        let errorMessage = this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_UPDATING_MESSAGE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(errorMessage, this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_UPDATING_TITLE'));
      });

    this.loading.set(false);
  }

  deleteMessageTemplate(name: string): void {
    this.loading.set(true);
    this.#wireApiService.messageTemplatesApi.deleteMessageTemplate({ name: name })
      .then(() => {
        this.loadMessageTemplates();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_DELETED_MESSAGE'),
          this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.TEMPLATE_DELETED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error deleting message template:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_DELETING_MESSAGE');
        const errorTitle = this.#translateService.instant('SETTINGS.MESSAGE_TEMPLATES.ERROR_DELETING_TITLE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(errorMessage, errorTitle);
      });

    this.loading.set(false);
  }

}
